// ── Post engagement drill-down (web Social Moderation) ───────────────────────
//
// Who liked, who commented on and who has seen one post — the three lists behind the
// clickable counts on the All posts feed, table and review modal. Every route lives
// under /social-moderation/posts/:postId and is authorised per call on `projectId` by
// the same verified-moderator check as the rest of that page:
//   • 403 = not a moderator here (never 401: the shared axios instance turns any 401
//     into the app-wide "session expired" sign-out — see services/api.ts);
//   • 404 = the post is not in this project.
//
// Moderators see EVERY post state (live, removed, held, not approved, deleted by its
// author) and every comment state, which is why a comment row carries its own
// `status`: the employee-facing thread drops exactly the ones this list must show.
//
// Cursor-paged (keyset on the list's own timestamp, newest first), not offset-paged:
// a popular post gains likes and views while a moderator is scrolling, and an offset
// would then repeat or skip people at every page boundary.
//
// Like connectPolicyAPI, these methods UNWRAP the `{ success, data }` envelope and
// resolve to `data`; failures reject with the axios error, so callers read the status
// with connectPolicyErrorStatus / connectPolicyErrorMessage.
//
// Console-only: the mobile app keeps reading the employee feed endpoints, which apply
// audience rules this console does not, and nothing they return was changed for this.

import type { AxiosResponse } from 'axios';
import api from './api';

/** Which list. 'viewers' is the "Seen by" tab. */
export type EngagementTab = 'likes' | 'comments' | 'viewers';

/** Render order of the panel's tabs. */
export const ENGAGEMENT_TABS: EngagementTab[] = ['likes', 'comments', 'viewers'];

/** One person on a list, hydrated the same way the page's post authors are. */
export interface EngagementPerson {
  empCode: string;
  /** Falls back to the employee code server-side when the directory has no name. */
  name: string;
  designation: string | null;
  storeName: string | null;
  /** Profile-photo FILENAME, not a URL — see engagementPhotoUrl. */
  photo: string | null;
  /** Likes list: when they liked it. */
  likedAt?: string;
  /** Seen-by list: when they FIRST saw it — a view is counted once per person. */
  viewedAt?: string;
}

/** The comment's state NOW. Only 'live' is on anyone's feed; the rest are shown to
 *  moderators with a chip. */
export type EngagementCommentStatus = 'live' | 'removed' | 'author_deleted' | 'held' | 'not_approved';

/** One comment, with its author. A person who commented three times is three rows. */
export interface EngagementComment extends Omit<EngagementPerson, 'likedAt' | 'viewedAt'> {
  commentId: string;
  content: string;
  createdAt: string;
  status: EngagementCommentStatus;
}

export interface EngagementRowByTab {
  likes: EngagementPerson;
  comments: EngagementComment;
  viewers: EngagementPerson;
}

export type EngagementRow = EngagementPerson | EngagementComment;

export interface EngagementPage<T> {
  rows: T[];
  /** Everything matching `q` (not just this page) — the tab count when `q` is empty. */
  total: number;
  /** Opaque; null = this was the last page. */
  nextCursor: string | null;
}

export interface EngagementListParams {
  /** Name or employee code, matched server-side. */
  q?: string;
  cursor?: string | null;
  /** ≤ ENGAGEMENT_PAGE_MAX; the server defaults to 50. */
  limit?: number;
  signal?: AbortSignal;
}

/** The server's per-request ceiling. */
export const ENGAGEMENT_PAGE_MAX = 200;

/** Rows a CSV download stops at. Past this a spreadsheet is not how anyone reads it —
 *  and 50 sequential requests is already a long wait. */
export const ENGAGEMENT_EXPORT_CAP = 10_000;

const PATH: Record<EngagementTab, string> = {
  likes: 'likes',
  comments: 'comments',
  viewers: 'viewers',
};

interface Envelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
}

/** `{ success, data }` → a normalised page. A 2xx that still says success:false is a
 *  failure rather than an empty list — "nobody liked this" must never be the way a
 *  broken response renders. */
const unwrapPage = <T>(res: AxiosResponse<Envelope<Partial<EngagementPage<T>>>>): EngagementPage<T> => {
  const body = res.data;
  if (!body || body.success === false || !body.data || !Array.isArray(body.data.rows)) {
    throw new Error(body?.message || 'Unexpected response from the server.');
  }
  const { rows } = body.data;
  const total = Number(body.data.total);
  return {
    rows,
    total: Number.isFinite(total) ? total : rows.length,
    nextCursor: body.data.nextCursor || null,
  };
};

/** One page of one list. */
const list = async <K extends EngagementTab>(
  projectId: number,
  postId: string,
  tab: K,
  params: EngagementListParams = {},
): Promise<EngagementPage<EngagementRowByTab[K]>> => {
  const q = params.q?.trim();
  return unwrapPage<EngagementRowByTab[K]>(await api.get(
    `/social-moderation/posts/${encodeURIComponent(postId)}/${PATH[tab]}`,
    {
      params: {
        projectId,
        ...(q ? { q } : {}),
        ...(params.cursor ? { cursor: params.cursor } : {}),
        ...(params.limit !== undefined ? { limit: params.limit } : {}),
      },
      signal: params.signal,
    },
  ));
};

/**
 * Every row of one list (honouring `q`), paged through at the server's maximum and
 * stopped at `cap`. `truncated` says rows were left behind, so the caller can say so
 * instead of handing over a silently short file. Stops, too, if the server ever
 * repeats a cursor — a loop that never ends is worse than a short export.
 */
const listAll = async <K extends EngagementTab>(
  projectId: number,
  postId: string,
  tab: K,
  opts: { q?: string; cap?: number; signal?: AbortSignal; onProgress?: (fetched: number) => void } = {},
): Promise<{ rows: Array<EngagementRowByTab[K]>; total: number; truncated: boolean }> => {
  const cap = opts.cap ?? ENGAGEMENT_EXPORT_CAP;
  const rows: Array<EngagementRowByTab[K]> = [];
  const seen = new Set<string>();
  let cursor: string | null = null;
  let total = 0;
  let more = true;
  while (more && rows.length < cap) {
    const page: EngagementPage<EngagementRowByTab[K]> = await list(projectId, postId, tab, {
      q: opts.q, cursor, limit: Math.min(ENGAGEMENT_PAGE_MAX, cap - rows.length), signal: opts.signal,
    });
    total = page.total;
    rows.push(...page.rows.slice(0, cap - rows.length));
    opts.onProgress?.(rows.length);
    more = !!page.nextCursor && page.rows.length > 0 && !seen.has(page.nextCursor);
    if (page.nextCursor) seen.add(page.nextCursor);
    cursor = page.nextCursor;
  }
  return { rows, total, truncated: more };
};

export const engagementAPI = { list, listAll };

export default engagementAPI;

// ── Helpers ──────────────────────────────────────────────────────────────────

export const isEngagementComment = (row: EngagementRow): row is EngagementComment =>
  (row as EngagementComment).commentId != null;

/** The one timestamp a row is about: liked, first seen, or commented. */
export const engagementRowTime = (row: EngagementRow): string | null =>
  isEngagementComment(row) ? row.createdAt ?? null : row.likedAt ?? row.viewedAt ?? null;

const PHOTO_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif']);

/**
 * A viewable URL for a profile-photo FILENAME, or null.
 *
 * The same route mobile's buildAwsHrImageUrl uses: the backend's
 * /oneoffice/profile-photo proxy over 1Office's hr_images folder, which attaches the
 * client token server-side and needs no bearer, so a plain <img src> works. A name
 * with no image extension resolves to no photo rather than a broken one; callers
 * still fall back to initials on a load error (a HEIC in a non-Safari browser, a
 * photo since removed from HR).
 */
export const engagementPhotoUrl = (photo: string | null | undefined): string | null => {
  const filename = String(photo ?? '').trim();
  if (!filename) return null;
  const bare = filename.split(/[?#]/)[0];
  const dot = bare.lastIndexOf('.');
  if (dot < 0 || dot === bare.length - 1) return null;
  if (!PHOTO_EXTS.has(bare.slice(dot + 1).toLowerCase())) return null;
  const base = String(api.defaults.baseURL ?? '/api').replace(/\/+$/, '');
  return `${base}/oneoffice/profile-photo?path=${encodeURIComponent('images/hr_images/')}&filename=${encodeURIComponent(filename)}`;
};
